import React, {useCallback, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {DropDown, ModalConfirmation} from '@dized/ui';

import {GridButton, HeaderActions} from '../DataGrid.js';
import {DropdownPicker} from '../DropdownPicker.js';
import {StyledTable} from '../StyledComponents.js';

import {getLocaleName, getSortedLocaleList, sortedLocaleList} from './LocaleList';

/**
 * LocaleTable - Displays and manages language locales in a table format
 * 
 * USAGE:
 * - localeActions: Function that returns JSX with action buttons for NON-MASTER locales
 *   The Delete button is automatically added first, your custom actions (Review, Edit) come after
 *   Example: (locale, localeData) => (<><Button>Review</Button><Button>Edit</Button></>)
 * 
 * - localeMasterActions: Function that returns JSX with action buttons for MASTER locales
 *   No Delete button is added automatically for master locales
 *   Example: (locale, localeData) => (<Button>Edit Project</Button>)
 * 
 * Note: Download functionality has been extracted to TranslationDownload component
 */

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: right;
  width: 100%;
`;

export const LocaleTable = ({
  locales,
  localeActions,
  localeMasterActions,
  onAddLocale,
  onDeleteLocale,
  onUpdateLocale,
  onEditProject,
}) => {
  const [deleteLocale, updateDeleteLocale] = useState(undefined);
  const [availableLocales, setAvailableLocales] = useState(sortedLocaleList);

  const clearDeleteLocale = useCallback(
    () => updateDeleteLocale(),
    []
  );

  // Load the latest locale list from remote source
  useEffect(() => {
    getSortedLocaleList()
      .then(setAvailableLocales)
      .catch(error => {
        console.warn('Failed to load remote locales, using fallback:', error);
        // Keep using the fallback sortedLocaleList
      });
  }, []);

  const localeMap = locales.reduce(
    (a, c) => {
      const {locale, master = false} = c;

      a[locale] = {
        ...c,
        master,
        name: getLocaleName(locale),
      };

      return a;
    },
    {}
  );
  
  // Transform availableLocales to dropdown options, filtering out already used locales
  const languageOptions = availableLocales
    .filter(locale => !Object.hasOwnProperty.call(localeMap, locale.value))
    .map(locale => ({
      label: `${locale.label} (${locale.value})`,
      value: locale.value
    }));

  const handleAddLocale = (localeCode) => {
    if (onAddLocale) {
      onAddLocale(localeCode);
    }
  };

  return (
    <>
      <StyledTable
        columns={[
          {
            title: 'Locale',
            template: '1fr',
            renderCell: d => d.name,
          },
          {
            title: 'Status',
            // eslint-disable-next-line react/display-name, react/prop-types
            renderCell: ({locale, master, state}) => master ? (
              <span>Main Locale</span>
            ) : (
              <DropDown
                id={`locale-status-${locale}`}
                name={`locale-status-${locale}`}
                onChange={event => onUpdateLocale(locale, {state: event.target.value})}
                options={[
                  {label: 'Draft',       value: 'draft'},
                  {label: 'Unpublished', value: 'unpublished'},
                  {label: 'Published',   value: 'published'},
                ]}
                value={state ?? 'draft'}
              />
            )
          },
          {
            title: '',
            renderHeader: () => (
              <HeaderActions>
                <DropdownPicker
                  options={languageOptions}
                  title="Select new language to add"
                  onChange={handleAddLocale}
                  searchable={true}
                  searchPlaceholder="Search languages..."
                />
              </HeaderActions>
            ),
            // eslint-disable-next-line react/display-name
            renderCell: d => (
              <ActionsContainer>
                {d.master ? (
                  // Master locale actions
                  localeMasterActions ? 
                    localeMasterActions(d.locale, d) : 
                    localeActions ? 
                      localeActions(d.locale, d) : 
                      onEditProject && <GridButton onClick={() => onEditProject()}>Edit</GridButton>
                ) : (
                  // Non-master locale actions: delete button + custom actions
                  <>
                    <GridButton onClick={() => updateDeleteLocale(d)}>Delete</GridButton>
                    {localeActions && localeActions(d.locale, d)}
                  </>
                )}
              </ActionsContainer>
            ),
          },
        ]}
        data={Object.values(localeMap).sort((a, b) => {
          // Master locale always goes first
          if (a.master && !b.master) return -1;
          if (!a.master && b.master) return 1;
          // If both are master or both are not master, sort by name
          return a.name.localeCompare(b.name);
        })}
      />
      {deleteLocale && (
        <ModalConfirmation
          header={`Delete language '${deleteLocale.name}'`}
          labelConfirm='Delete'
          onCancel={clearDeleteLocale}
          onClose={clearDeleteLocale}
          onConfirm={() => {
            clearDeleteLocale();
            onDeleteLocale(deleteLocale.locale);
          }}
          visible
        >
          Do you really want to remove this language from the project?
        </ModalConfirmation>
      )}
    </>
  );
}

LocaleTable.propTypes = {
  /** GET_XXX_LOCALES() response.value.data */
  locales: PropTypes.arrayOf(
    PropTypes.shape({
      locale: PropTypes.string.isRequired,
      master: PropTypes.bool,
      state: PropTypes.string,
    })
  ).isRequired,
  /** Function that returns JSX with action buttons for non-master locales (delete button is automatically added) */
  localeActions: PropTypes.func.isRequired,
  /** Function that returns JSX with action buttons for master locales */
  localeMasterActions: PropTypes.func,
  onAddLocale: PropTypes.func.isRequired,
  onDeleteLocale: PropTypes.func.isRequired,
  onUpdateLocale: PropTypes.func.isRequired,
  onEditProject: PropTypes.func,
};
